import React from 'react'

import { REBUILD_CLASSIFICATIONS, optimizeImageSrc } from '../utils/shared.js';
import QuoteConfirmationModal from './QuoteConfirmationModal.jsx';
import useAnalytics from '../hooks/useAnalytics';

// --- NEW: Rebuild Matrix Data Structure ---
const REBUILD_MATRIX_DATA = {
    "Powertrain & Drivetrain": ["Engine", "Transmission", "Drive", "Differential", "Clutch / Torque Converter", "Turbo / Supercharger", "Exhaust System", "Pump Drive", "Custom 1 (Powertrain)", "Custom 2 (Powertrain)", "Custom 3 (Powertrain)", "Custom 4 (Powertrain)", "Custom 5 (Powertrain)"],
    "Hydraulic, Fuel & Fluid Systems": ["Hydraulic System (Pumps, Motors, Valves, Piping)", "Fuel System (Pump, Sensors, Valves, Piping)", "Diesel Supply / Tanks", "Piping & Fittings", "Cooling System (Radiator, Fans, Piping)", "Lubrication Systems", "Fluid Flush & Replacement", "Air System (Pump, Motor, Valves, Sensors, Piping)", "Feed Beam", "Stabilizers", "Reeler", "Custom 1 (Hydraulic)", "Custom 2 (Hydraulic)", "Custom 3 (Hydraulic)", "Custom 4 (Hydraulic)", "Custom 5 (Hydraulic)"],
    "Electrical & Electronic Systems": ["Wiring", "ECU / Control Modules", "Sensors & Actuators", "Battery & Charging System", "Starter & Alternator", "Display Screens & Instrument Panels", "Switches, Joysticks & Controls", "GPS / Telematics Systems", "Lights", "CAS Level 9 System", "Slip Ring Cabinet", "Custom 1 (Electrical)", "Custom 2 (Electrical)", "Custom 3 (Electrical)", "Custom 4 (Electrical)", "Custom 5 (Electrical)"],
    "Structure, Chassis & Frame": ["Mainframe", "MAST/FEED", "Cab / ROPS / FOPS", "Articulated Carrier", "Custom 1 (Structure)", "Custom 2 (Structure)", "Custom 3 (Structure)", "Custom 4 (Structure)", "Custom 5 (Structure)"],
    "Safety Systems": ["Fire Suppression", "Emergency Stop Systems", "Steps, Handrails & Guarding", "Seat & Seatbelt", "Custom 1 (Safety)", "Custom 2 (Safety)", "Custom 3 (Safety)", "Custom 4 (Safety)", "Custom 5 (Safety)"],
    "Wear Components & Running Gear": ["Tyres", "Rims & Wheels", "GET (Ground Engaging Tools)", "Bearings & Bushes", "Pins & Liners", "Tracks / Undercarriage (If Applicable)", "Custom 1 (Wear)", "Custom 2 (Wear)", "Custom 3 (Wear)", "Custom 4 (Wear)", "Custom 5 (Wear)"],
    "Bodywork, Cab & Visibility": ["Bodywork & Paint", "Windows", "Interior Trim", "Decals & Branding", "Corrosion Treatment", "Custom 1 (Bodywork)", "Custom 2 (Bodywork)", "Custom 3 (Bodywork)", "Custom 4 (Bodywork)", "Custom 5 (Bodywork)"],
    "Compliance, Legal & Identification": ["DMR Compliance", "Roadworthiness / Safety Certification", "Signage", "Steering Wheel", "Pedals", "Custom 1 (Compliance)", "Custom 2 (Compliance)", "Custom 3 (Compliance)", "Custom 4 (Compliance)", "Custom 5 (Compliance)"]
};

// --- NEW: Helper for details in matrix ---
const DetailDisplay = ({ label, value }) => {
    if (!value && value !== 0) return null;
    return <p><span className="font-semibold">{label}:</span> {value}</p>;
};

// --- NEW: Add local formatPrice for robustness ---
const formatPrice = (price) => {
    const num = parseFloat(price);
    if (isNaN(num)) return 'R 0.00';
    return `R ${num.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// --- NEW: Helper to format description with line breaks ---
function formatDescription(description) {
    if (!description) return null;
    const lines = description.split('\n');
    return lines.map((line, i) => (
        <React.Fragment key={i}>
            {line}
            {i < lines.length - 1 && <br />}
        </React.Fragment>
    ));
}

function getBearingRichDescription(type) {
    if (!type) return null;
    
    if (type === 'Cylindrical Roller Bearing') {
        return (
            <div className="text-gray-600 text-sm space-y-4 mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                <p>KG Ultra Cylindrical Roller Bearings are engineered for high load-carrying capacity, precision, and durability in demanding industrial environments. These bearings use cylindrical rollers instead of balls, enabling them to handle heavier radial loads with minimal friction and high-speed performance.</p>
                <div>
                    <h4 className="font-bold text-gray-800 mb-1">Features</h4>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>High Radial Load Capacity:</strong> The line contact between rollers and raceways allows these bearings to support much higher radial loads than ball bearings.</li>
                        <li><strong>High-Speed Capability:</strong> With minimal friction and efficient roller geometry, these bearings can operate at high speeds with reduced heat generation.</li>
                        <li><strong>Multiple Designs and Configurations:</strong> Available in types such as NU, N, NJ, and NUP, which offer flexibility in axial movement and fixed positioning depending on the design.</li>
                        <li><strong>Separability for Easy Mounting:</strong> Most bearings have separable inner and outer rings, simplifying installation, disassembly, and inspection.</li>
                        <li><strong>Precision Manufacturing:</strong> Manufactured with tight tolerances and surface finishes for smooth operation, long life, and minimal vibration.</li>
                    </ul>
                </div>
                <div>
                    <h4 className="font-bold text-gray-800 mb-1">Applications</h4>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Mining & Cement</li>
                        <li>Electric Motors and Generators</li>
                        <li>Gearboxes and Transmissions</li>
                        <li>Steel Mills and Heavy Machinery</li>
                        <li>Machine Tools and CNC Equipment</li>
                        <li>Railway Axles and Rolling Stock</li>
                        <li>Pumps and Compressors</li>
                        <li>Pulp & Paper</li>
                    </ul>
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <h4 className="font-bold text-blue-800 mb-2">Why Choose KG Ultra Bearings?</h4>
                    <ul className="list-disc pl-5 space-y-1 text-blue-900">
                        <li><strong>ISO tolerance class 6:</strong> ISO 492, P6, Tighter tolerance than normal</li>
                        <li><strong>Large assortments:</strong> With ISO 5593:2023 dimensions and control</li>
                        <li><strong>Premium quality steel:</strong> Made with GCr15 equivalent to AISI 52100 and DIN 100Cr6 (SKF grade 3)</li>
                        <li><strong>Unparalleled technical and after sales support</strong></li>
                    </ul>
                </div>
            </div>
        );
    }
    if (type === 'Double Direction Tapered Roller Thrust Bearing') {
        return (
            <div className="text-gray-600 text-sm space-y-4 mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                <p>KG Ultra Tapered Roller Thrust bearings, are designed exclusively for axial load support. These bearings provide exceptional axial rigidity and are ideal for handling heavy loads on vertical shaft. Standard designs feature machined brass cages, ensuring durability and reliable performance.</p>
                <div>
                    <h4 className="font-bold text-gray-800 mb-1">Features</h4>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>Exclusive Axial Load Support:</strong> Designed specifically to handle heavy axial loads, not radial loads.</li>
                        <li><strong>High Axial Rigidity:</strong> Provides exceptional stiffness for precise axial positioning under high load.</li>
                        <li><strong>Durable Machined Brass Cages:</strong> Ensures reliable performance and long service life in demanding conditions.</li>
                    </ul>
                </div>
                <div>
                    <h4 className="font-bold text-gray-800 mb-1">Applications</h4>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Mining</li>
                        <li>Marine Propulsion Systems</li>
                        <li>Steel and Paper Mills</li>
                        <li>Vertical Pumps and Compressors</li>
                        <li>Machine Tool Spindles</li>
                        <li>Oil Drilling Rigs and Equipment</li>
                        <li>Rotary Tables and Industrial Turntables</li>
                    </ul>
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <h4 className="font-bold text-blue-800 mb-2">Why Choose KG Ultra Bearings?</h4>
                    <ul className="list-disc pl-5 space-y-1 text-blue-900">
                        <li><strong>ISO tolerance class 6:</strong> ISO 492, P6, Tighter tolerance than normal</li>
                        <li><strong>Large assortments:</strong> With ISO 5593:2023 dimensions and control</li>
                        <li><strong>Premium quality steel:</strong> Made with GCr15 equivalent to AISI 52100 and DIN 100Cr6 (SKF grade 3)</li>
                        <li><strong>Unparalleled technical and after sales support</strong></li>
                    </ul>
                </div>
            </div>
        );
    }
    if (type === 'Double Row Angular Contact Ball Bearing') {
        return (
            <div className="text-gray-600 text-sm space-y-4 mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                <p>KG offers a comprehensive range of Double Row Angular Contact Ball Bearings, engineered to provide high performance and reliability across various applications. These bearings are designed to accommodate both radial and axial loads in both directions, making them particularly suitable for applications requiring rigid axial guidance.</p>
                <div>
                    <h4 className="font-bold text-gray-800 mb-1">Key Features</h4>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>Design and Construction:</strong> Double Row Angular Contact Ball Bearings feature inner and outer rings with raceways displaced relative to each other, forming an angle of contact between 15° and 45° concerning the bearing axis. This design enables the bearings to handle combined loads efficiently.</li>
                        <li><strong>Load Capacity:</strong> The dual-row configuration allows these bearings to support high radial loads and axial loads in both directions, enhancing their versatility in various industrial applications.</li>
                        <li><strong>Material and Quality:</strong> Manufactured from premium Chrome Steel SAE52100/100Cr6/GCr15 or Stainless Steel (420 and 440), these bearings ensure durability and resistance to wear. They are available in multiple quality grades, including ABEC 1, ABEC 3, ABEC 5, and ABEC 7, catering to different precision requirements.</li>
                        <li><strong>Seals and Shields:</strong> Available in open and 2RS (sealed) configurations, these bearings offer flexibility based on application needs, providing protection against contaminants and retaining lubrication.</li>
                        <li><strong>Cage Options:</strong> Equipped with machined brass cages, these bearings ensure optimal performance under varying operational conditions.</li>
                    </ul>
                </div>
                <div>
                    <h4 className="font-bold text-gray-800 mb-1">Applications</h4>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Mining: Heavy-duty mud pumps, vertical turbines, drilling equipment</li>
                        <li>Metal Industry: Rolling mills and continuous casting machines</li>
                        <li>Fluid Machinery: Pumps and compressors</li>
                        <li>Aerospace Applications: Aircraft engines and landing gear systems</li>
                        <li>Machine Tool Applications: Spindle units and precision machinery</li>
                        <li>Pumps and Motors: Industrial pumps and electric motors</li>
                        <li>Railroad Applications: Axle boxes and traction motors</li>
                    </ul>
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <h4 className="font-bold text-blue-800 mb-2">Why Choose KG Ultra Bearings?</h4>
                    <ul className="list-disc pl-5 space-y-1 text-blue-900">
                        <li><strong>ISO tolerance class 6:</strong> ISO 492, P6, Tighter tolerance than normal</li>
                        <li><strong>Large assortments:</strong> With ISO 5593:2023 dimensions and control</li>
                        <li><strong>Premium quality steel:</strong> Made with GCr15 equivalent to AISI 52100 and DIN 100Cr6 (SKF grade 3)</li>
                        <li><strong>Unparalleled technical and after sales support</strong></li>
                    </ul>
                </div>
            </div>
        );
    }
    if (type === 'Four Row Cylindrical Roller Bearing' || type === 'Inner Ring Four Row Cylindrical Roller Bearing') {
        return (
            <div className="text-gray-600 text-sm space-y-4 mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                <p>KG Ultra Four Row Cylindrical Roller Bearings are engineered for extremely high radial load capacity and are predominantly used in heavy-duty industrial applications, particularly rolling mills. The four-row design allows for optimal load distribution across multiple roller rows, resulting in high rigidity, excellent operational accuracy, and long service life under severe working conditions.</p>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <h4 className="font-bold text-blue-800 mb-2">Why Choose KG Ultra Bearings?</h4>
                    <ul className="list-disc pl-5 space-y-1 text-blue-900">
                        <li><strong>Unparalleled technical and after sales support</strong></li>
                        <li><strong>ISO tolerance class 6:</strong> ISO 492, P6, Tighter tolerance than normal</li>
                        <li><strong>Premium quality steel:</strong> Made with GCr15 equivalent to AISI 52100 and DIN 100Cr6 (SKF grade 3)</li>
                        <li><strong>Large assortments:</strong> With ISO 5593:2023 dimensions and control</li>
                    </ul>
                </div>
            </div>
        );
    }
    if (type === 'Self-Aligning Ball Bearing' || type === 'Self Aligning Ball Bearing') {
        return (
            <div className="text-gray-600 text-sm space-y-4 mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                <p>KG Ultra Self-Aligning Ball Bearings are engineered to deliver exceptional performance in applications where shaft misalignment or deflection may occur. These bearings are designed to accommodate angular misalignment while maintaining smooth and efficient operation.</p>
                <div>
                    <h4 className="font-bold text-gray-800 mb-1">Features</h4>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>Self-Alignment Capability:</strong> Automatically compensates for shaft misalignment and deflection.</li>
                        <li><strong>Low Friction:</strong> Optimized for smooth operation and reduced heat generation.</li>
                        <li><strong>High-Speed Performance:</strong> Suitable for applications requiring consistent high-speed operation.</li>
                        <li><strong>Durability:</strong> Manufactured using high-quality materials for extended bearing life.</li>
                    </ul>
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <h4 className="font-bold text-blue-800 mb-2">Why Choose KG Ultra Bearings?</h4>
                    <ul className="list-disc pl-5 space-y-1 text-blue-900">
                        <li><strong>ISO tolerance class 6:</strong> ISO 492, P6, Tighter tolerance than normal</li>
                        <li><strong>Large assortments:</strong> With ISO 5593:2023 dimensions and control</li>
                        <li><strong>Premium quality steel:</strong> Made with GCr15 equivalent to AISI 52100 and DIN 100Cr6 (SKF grade 3)</li>
                        <li><strong>Unparalleled technical and after sales support</strong></li>
                    </ul>
                </div>
            </div>
        );
    }
    if (type === 'Spherical Roller Bearing') {
        return (
            <div className="text-gray-600 text-sm space-y-4 mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                <p>KG Ultra Spherical Roller Bearings feature two rows of symmetrical rollers and a common sphered raceway in the outer ring. This unique design allows the bearings to self-align and maintain performance even in cases of shaft deflection and misalignment, accommodating heavy radial and axial loads.</p>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <h4 className="font-bold text-blue-800 mb-2">Why Choose KG Ultra Bearings?</h4>
                    <ul className="list-disc pl-5 space-y-1 text-blue-900">
                        <li><strong>ISO tolerance class 6:</strong> ISO 492, P6, Tighter tolerance than normal</li>
                        <li><strong>Large assortments:</strong> With ISO 5593:2023 dimensions and control</li>
                        <li><strong>Premium quality steel:</strong> Made with GCr15 equivalent to AISI 52100 and DIN 100Cr6 (SKF grade 3)</li>
                        <li><strong>Unparalleled technical and after sales support</strong></li>
                    </ul>
                </div>
            </div>
        );
    }
    return null;
}

export default function FullScreenProductModal({ product, isOpen, onClose, user, basket, onAddToBasket, onUpdateBasketQuantity, onRemoveFromBasket, onShowLogin, savedItems, onToggleSave }) {
    const { trackEvent, consent } = useAnalytics();
    // const [quantity, setQuantity] = React.useState(1); // Removed local quantity state to use global basket state
    // --- NEW: State for modal-specific actions ---
    const [isProcessing, setIsProcessing] = React.useState(false);
    const [actionError, setActionError] = React.useState(null);
    const [actionSuccess, setActionSuccess] = React.useState(null);
    const [showPriceBreakdown, setShowPriceBreakdown] = React.useState(false);
    const [showContactModal, setShowContactModal] = React.useState(false);
    const [showDieselQuoteModal, setShowDieselQuoteModal] = React.useState(false);
    const [priceRequested, setPriceRequested] = React.useState(false);
    const [showQuoteConfirmModal, setShowQuoteConfirmModal] = React.useState(false);
    // --- NEW: State for consumable size selector ---
    const [availableSizes, setAvailableSizes] = React.useState(null);
    const [selectedSizes, setSelectedSizes] = React.useState({});

    // Consumables may store either numeric stock or { qty, price } per size.
    const getSizeStock = (size) => {
        const entry = availableSizes?.[size];
        const stock = typeof entry === 'object' && entry !== null ? entry.qty : entry;
        const value = Number(stock);
        return Number.isFinite(value) ? Math.max(0, value) : 0;
    };

    const getSizeUnitPrice = (size) => {
        const entry = availableSizes?.[size];
        const override = typeof entry === 'object' && entry !== null ? Number(entry.price) : NaN;
        return Number.isFinite(override) && override > 0
            ? override
            : (Number(product?.price) || 0);
    };

    const selectedQuantity = Object.values(selectedSizes).reduce(
        (sum, qty) => sum + (Number(qty) || 0), 0
    );
    const selectedTotal = Object.entries(selectedSizes).reduce(
        (sum, [size, qty]) => sum + (Number(qty) || 0) * getSizeUnitPrice(size), 0
    );


    // Track modal open and product view
    React.useEffect(() => {
        if (isOpen && consent === 'granted') {
            trackEvent('product_modal_opened', {
                productId: product.id,
                listingType: product.listingType,
                listingNumber: product.listing_number || '',
                productName: product.name || '',
                categoryId: product.category_id ?? null,
                categoryName: product.category_name || '',
            });
            trackEvent('product_viewed', {
                productId: product.id,
                listingType: product.listingType,
                listingNumber: product.listing_number || '',
                productName: product.name || '',
                categoryId: product.category_id ?? null,
                categoryName: product.category_name || '',
                eventData: {
                    unitPrice: product.price ?? null,
                },
            });
        }
    }, [isOpen, product, consent, trackEvent]);
    const cleanPath = (path) => {
        if (!path) return null;
        // Allow web URLs and data URIs
        if (path.match(/^(http|https|data):/i)) return path;
        
        // 1. Decode URI component to handle %20 correctly
        let clean = decodeURIComponent(path).replace(/\\/g, '/').trim();
        
        // 2. Remove full project path if present at the beginning
        if (clean.startsWith('/miners-market-main/')) {
            clean = clean.substring('/miners-market-main/'.length);
        }
        
        // Strategy: Locate 'uploads/' or 'images/' to anchor the path relative to project root
        const anchorMatch = clean.match(/(uploads\/|images\/|miscellaneous items\/)/i);
        if (anchorMatch) {
            clean = clean.substring(anchorMatch.index);
        }
        if (clean.includes(':')) return null;
        // Ensure absolute path starting with /
        if (!clean.startsWith('/')) {
            clean = '/' + clean;
        }
        return clean;
    };

    const productImages = React.useMemo(() => {
        if (!product) return [];
        const allImages = [];
        if (product.images && Array.isArray(product.images)) {
            allImages.push(...product.images);
        }
        // --- NEW: Handle Media Array ---
        if (product.media && Array.isArray(product.media)) {
            const mediaImages = product.media.filter(m => m.media_type === 'image').map(m => m.file_path);
            allImages.push(...mediaImages);
        }
        for (let i = 1; i <= 25; i++) {
            if (product[`image_url_${i}`]) {
                allImages.push(product[`image_url_${i}`]);
            }
        }
        // --- REFACTORED: Non-destructive image logic for bearings ---
        let finalImages = [...new Set(allImages.map(cleanPath).filter(Boolean))];
        
        if (product.bearing_type) { // Bearing logic - check for type instead of fragile ID
            const hasUserImages = finalImages.length > 0;

             if (product.bearing_type === 'Inner Ring Four Row Cylindrical Roller Bearing') {
                 const pkgImage = 'images/products/Inner Ring Four Row Cylindrical Roller Bearing Packaging.png';
                 if (!finalImages.includes(pkgImage)) finalImages.push(pkgImage);
             }
             if (product.bearing_type === 'Double Direction Tapered Roller Thrust Bearing') {
                if (!hasUserImages) {
                    finalImages = ['images/products/Tapered Roller Thrust Bearings.png'];
                }
            } else if (product.bearing_type.includes('Spherical Roller Bearing')) {
                if (!hasUserImages) {
                    finalImages = [
                        'Miscellaneous Items/KG ULTRA BEARING IMAGES/Spherical roller bearing.png',
                        'Miscellaneous Items/KG ULTRA BEARING IMAGES/Spherical roller bearing box.png'
                    ];
                }
            } else if (product.bearing_type === 'Self-Aligning Ball Bearing' || product.bearing_type === 'Self Aligning Ball Bearing') {
                if (!hasUserImages) {
                    finalImages = [
                        'images/products/Self-Aligning Ball Bearing.png',
                        'images/products/Self-Aligning Ball Bearings Box.png'
                    ];
                }
            } else {
                // For other bearings, just add the packaging if it's not there
                if (product.bearing_type === 'Inner Ring Four Row Cylindrical Roller Bearing') {
                    const pkgImage = 'images/products/Inner Ring Four Row Cylindrical Roller Bearing Packaging.png';
                    if (!finalImages.includes(pkgImage)) finalImages.push(pkgImage);
                }
                const fallbackLogo = 'images/logo/MM - Logo.jpeg';
                if (!finalImages.includes(fallbackLogo)) {
                    finalImages.push(fallbackLogo);
                }
             }
        }
        
        return finalImages.length > 0 ? finalImages : ['images/logo/MM - Logo.jpeg'];
    }, [product]);

    const [mainImage, setMainImage] = React.useState(productImages.length > 0 ? productImages[0] : null);
    const [currentImageIndex, setCurrentImageIndex] = React.useState(0);

    const handleImageNav = (e, direction) => {
        if (e && e.stopPropagation) e.stopPropagation();
        const nextIndex = (currentImageIndex + direction + productImages.length) % productImages.length;
        setCurrentImageIndex(nextIndex);
        setMainImage(productImages[nextIndex]);
    };

    const [touchStart, setTouchStart] = React.useState(0);
    const handleTouchStart = (e) => setTouchStart(e.targetTouches[0].clientX);
    const handleTouchEnd = (e) => {
        const touchEnd = e.changedTouches[0].clientX;
        const diff = touchStart - touchEnd;
        if (Math.abs(diff) > 50) handleImageNav(null, diff > 0 ? 1 : -1);
    };

    const handleShare = async (e) => {
        if (e) e.stopPropagation();
        const shareUrl = `${window.location.origin}/${product.listingType === 'master_bearing' ? `bearing/${product.slug}` : `?viewProduct=${product.id}&viewType=${product.listingType}`}`;
        
        const shareText = `Check out this listing on Miners Market:\n\n${product.name}\nListing #: ${product.listing_number}\n\n${shareUrl}`;

        // Try Web Share API first (mobile devices)
        if (navigator.share) {
            try {
                await navigator.share({
                    title: product.name,
                    text: shareText,
                    url: shareUrl
                });
                return;
            } catch (err) {
                if (err.name !== 'AbortError') {
                    console.warn('Web Share API failed, falling back to clipboard:', err);
                }
            }
        }

        // Fallback: Copy to clipboard with user feedback
        try {
            await navigator.clipboard.writeText(shareText);
            
            // Create a temporary toast notification
            const toast = document.createElement('div');
            toast.className = 'fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in';
            toast.textContent = 'Link copied to clipboard!';
            document.body.appendChild(toast);
            
            setTimeout(() => {
                toast.style.opacity = '0';
                toast.style.transition = 'opacity 0.3s';
                setTimeout(() => document.body.removeChild(toast), 300);
            }, 2000);
        } catch (err) {
            console.error('Clipboard copy failed:', err);
            alert('Failed to copy link. Please copy manually:\n\n' + shareText);
        }
    };

    // --- NEW: Parse checklist items safely for display ---
    const checklistItems = React.useMemo(() => {
        if (!product.component_checklist) return null;
        try {
            const list = typeof product.component_checklist === 'string' ? JSON.parse(product.component_checklist) : product.component_checklist;
            return Array.isArray(list) && list.length > 0 ? list : null;
        } catch (e) {
            console.error("Error parsing component_checklist", e);
            return null;
        }
    }, [product.component_checklist]);

    // --- NEW: Parse Rebuild Details safely ---
    const sellerRebuildDetails = React.useMemo(() => {
        if (product.listingType !== 'rebuild' || !product.rebuild_details) return {};
        try {
            return typeof product.rebuild_details === 'string' ? JSON.parse(product.rebuild_details) : product.rebuild_details;
        } catch (e) {
            return {};
        }
    }, [product.rebuild_details, product.listingType]);

    // --- NEW: Parse consumable size data ---
    React.useEffect(() => {
        if (product && product.listingType === 'consumable' && product.size && product.size.startsWith('{')) {
            try {
                const parsed = JSON.parse(product.size);
                setAvailableSizes(parsed);
                setSelectedSizes({});
            } catch (e) {
                setAvailableSizes(null);
            }
        } else {
            setAvailableSizes(null);
        }
    }, [product]);

    React.useEffect(() => {
        setImageError(false); // Reset image error on open/change
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            // setQuantity(1); 
        } else {
            document.body.style.overflow = 'auto';
            // Reset state when closing
            setShowPriceBreakdown(false);
            setActionError(null);
            setActionSuccess(null);
            setIsProcessing(false);
            const requested = localStorage.getItem(`price_requested_${product.listingType}_${product.id}`);
            setPriceRequested(!!requested);
            setMainImage(productImages.length > 0 ? productImages[0] : null);
            setCurrentImageIndex(0);
        }
        return () => document.body.style.overflow = 'auto';
    }, [isOpen, product, productImages]);

    // Effect to handle browser history for back button functionality
    React.useEffect(() => {
        if (isOpen) {
            // Push a new state to history when the modal opens
            window.history.pushState({ modal: true }, '');

            const handlePopState = (event) => {
                // If the user navigates back, close the modal
                if (!event.state || !event.state.modal) {
                    onClose();
                }
            };

            window.addEventListener('popstate', handlePopState);
            return () => {
                window.removeEventListener('popstate', handlePopState);
                // Clean up history state if modal is closed via UI button
                if (window.history.state && window.history.state.modal) window.history.back();
            };
        }
    }, [isOpen, onClose]);

    // Track KG product view
    React.useEffect(() => {
        if (isOpen && product) {
            // Check if this is a KG product (seller_id = 328 or category = miscellaneous)
            const isKGProduct = product.seller_id === 328 ||
                (product.listing_market === 'miscellaneous') ||
                (product.category_id === 126);

            if (isKGProduct) {
                const categoryMap = {
                    'filter': 'filters',
                    'bearing': 'bearings',
                    'conveyor': 'conveyor-belts',
                    'grease': 'greases',
                    'kg-pps': 'kg-pps',
                    'diesel': 'diesel'
                };
                const category = categoryMap[product.listingType] || 'general';

                try {
                    const payload = JSON.stringify([{
                        product_id: product.id,
                        product_type: product.listingType || 'general',
                        product_name: product.name,
                        category: category,
                        seller_id: product.seller_id || 328
                    }]);
                    navigator.sendBeacon('/api/track_kg_product_view.php', payload);
                } catch (e) {
                    console.error("Could not track KG product view:", e);
                }
            }
        }
    }, [isOpen, product]);

    const [imageError, setImageError] = React.useState(false);
    // Reset error when switching main image
    React.useEffect(() => {
        setImageError(false);
    }, [mainImage]);

    if (!isOpen || !product) return null;

    // --- BASKET LOGIC (Mirrored from ProductCard.js) ---
    const totalAmount = parseFloat(product.price) || 0; // This is the final price including fee
    const isBasketProduct = product.listingType === 'consumable' || (product.bearing_type && totalAmount > 0);
    const itemInBasket = isBasketProduct && basket ? basket.find(item => item.id === product.id && item.listingType === product.listingType) : null;
    const quantityInBasket = itemInBasket ? itemInBasket.quantity : 0;
    // --- NEW: Saved Item Logic ---
    const isSaved = savedItems.some(item => item.id === product.id && item.listingType === product.listingType);


    // --- NEW: Handle size quantity changes for consumables ---
    const handleSizeQtyChange = (size, delta) => {
        setSelectedSizes(prev => {
            const current = prev[size] || 0;
            const newValue = Math.max(0, Math.min(current + delta, getSizeStock(size)));
            if (newValue === 0) {
                const { [size]: _, ...rest } = prev;
                return rest;
            }
            return { ...prev, [size]: newValue };
        });
    };

    const handleAddToCart = () => {
        // Track KG product click for "Add to Basket"
        const isKGProduct = product.seller_id === 328 ||
            (product.listing_market === 'miscellaneous') ||
            (product.category_id === 126);
        if (isKGProduct) {
            const categoryMap = {
                'filter': 'filters',
                'bearing': 'bearings',
                'conveyor': 'conveyor-belts',
                'grease': 'greases',
                'kg-pps': 'kg-pps',
                'diesel': 'diesel'
            };
            const category = categoryMap[product.listingType] || 'general';
            try {
                const payload = JSON.stringify([{
                    product_id: product.id,
                    product_type: product.listingType || 'general',
                    product_name: product.name,
                    category: category,
                    seller_id: product.seller_id || 328
                }]);
                navigator.sendBeacon('/api/track_kg_product_click.php', payload);
            } catch (e) {
                console.error("Could not track KG product click:", e);
            }
        }

        // For consumables with size selection, pass selectedSizes
        if (availableSizes) {
            const totalQty = Object.values(selectedSizes).reduce((a, b) => a + b, 0);
            if (totalQty > 0) {
                onAddToBasket(product, totalQty, selectedSizes);
            }
        } else {
            onAddToBasket(product, 1);
        }
    };

    const handleIncreaseQuantity = () => {
        onUpdateBasketQuantity(product.id, product.listingType, quantityInBasket + 1);
    };

    const handleDecreaseQuantity = () => {
        if (quantityInBasket - 1 > 0) {
            onUpdateBasketQuantity(product.id, product.listingType, quantityInBasket - 1);
        } else {
            onRemoveFromBasket(product.id, product.listingType);
        }
    };

    // --- ACTION BUTTON LOGIC (Mirrored from ProductCard/ProductModal) ---
    const gateAction = () => {
        if (!user) {
            const redirectUrl = `${window.location.pathname}?viewProduct=${product.id}&viewType=${product.listingType}`;
            sessionStorage.setItem('redirectAfterLogin', redirectUrl);
            onShowLogin();
            return true;
        }
        return false;
    };

    const handleContactAdmin = () => {
        if (gateAction()) return;
        setShowContactModal(true);
    };

    const handleRequestPrice = async () => {
        if (gateAction()) return;
        setIsProcessing(true);
        setActionError(null);
        setActionSuccess(null);
        try {
            const response = await fetch('/api/request_price.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ product_id: product.id, listing_type: product.listingType })
            });
            const text = await response.text();
            let result = {};
            try {
                result = text ? JSON.parse(text) : {};
            } catch (e) {
                result = { error: 'Invalid response from server' };
            }
            if (!response.ok) throw new Error(result.error || 'Failed to request price.');
            setPriceRequested(true);
            localStorage.setItem(`price_requested_${product.listingType}_${product.id}`, 'true');
            setActionSuccess(result.message);
        } catch (err) {
            setActionError(err.message);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleRequestQuote = async () => {
        if (gateAction()) return;
        // Guard: Prevent "Request Quote" flow for items marked as "Price on Request"
        if (canRequestPrice) {
            setActionError("Pricing for this item is available upon request only. Please use the 'Request Price' button to inquire.");
            return;
        }

        // Track KG product click
        const isKGProduct = product.seller_id === 328 ||
            (product.listing_market === 'miscellaneous') ||
            (product.category_id === 126);
        if (isKGProduct) {
            const categoryMap = {
                'filter': 'filters',
                'bearing': 'bearings',
                'conveyor': 'conveyor-belts',
                'grease': 'greases',
                'kg-pps': 'kg-pps',
                'diesel': 'diesel'
            };
            const category = categoryMap[product.listingType] || 'general';
            try {
                const payload = JSON.stringify([{
                    product_id: product.id,
                    product_type: product.listingType || 'general',
                    product_name: product.name,
                    category: category,
                    seller_id: product.seller_id || 328
                }]);
                navigator.sendBeacon('/api/track_kg_product_click.php', payload);
            } catch (e) {
                console.error("Could not track KG product click:", e);
            }
        }

        setShowQuoteConfirmModal(true);
    };

    const handleDieselQuote = () => {
        if (gateAction()) return;
        setShowDieselQuoteModal(true);
    };

    const sendDieselQuote = async (formData) => {
        setIsProcessing(true);
        setActionError(null);
        setActionSuccess(null);
        try {
            const response = await fetch('/api/send_diesel_quote.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...formData,
                    product_name: product.name,
                    listing_number: product.listing_number
                })
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.error || 'Failed to send diesel quote.');

            setActionSuccess(result.message);
            setShowDieselQuoteModal(false);
        } catch (err) {
            setActionError(err.message);
        } finally {
            setIsProcessing(false);
        }
    };

    // --- NEW: Function to handle quote request after email confirmation ---
    const confirmAndRequestQuote = async (recipientEmail) => {
        setIsProcessing(true);
        setActionError(null);
        setActionSuccess(null);
        console.log('[PDF Generation FullScreen] Starting PDF generation for:', product.listingType);
        try {
            // Generate PDF similar to ProductModal.js
            const { jsPDF } = window.jspdf;
            const doc = new jsPDF();
            const minersMarketLogo = new Image();
            minersMarketLogo.src = 'images/logo/MM - Logo.jpeg';
            await new Promise(resolve => { minersMarketLogo.onload = resolve; });

            // Load product image
            let productImage = new Image();
            if (product.image_url_1 || product.image_url) {
                productImage.src = product.image_url_1 || product.image_url;
                await new Promise(resolve => { productImage.onload = resolve; });
            } else {
                productImage = null;
            }

            // Create circular logo
            const logoCanvas = document.createElement('canvas');
            const logoSizePx = Math.min(minersMarketLogo.width, minersMarketLogo.height);
            logoCanvas.width = logoSizePx;
            logoCanvas.height = logoSizePx;
            const logoCtx = logoCanvas.getContext('2d');
            logoCtx.beginPath();
            logoCtx.arc(logoSizePx / 2, logoSizePx / 2, logoSizePx / 2, 0, Math.PI * 2, true);
            logoCtx.closePath();
            logoCtx.clip();
            logoCtx.drawImage(minersMarketLogo, 0, 0, logoSizePx, logoSizePx);
            const circularLogoData = logoCanvas.toDataURL('image/png');

            // Add content to PDF
            doc.addImage(minersMarketLogo, 'JPEG', 15, 15, 30, 30);
            doc.setFontSize(22);
            doc.setFont('helvetica', 'bold');
            doc.text('OFFICIAL QUOTATION', doc.internal.pageSize.getWidth() / 2, 25, { align: 'center' });
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(100);
            
            doc.text(`Quote for: ${user.name}`, 15, 50);
            doc.text(`Date: ${new Date().toLocaleDateString()}`, doc.internal.pageSize.getWidth() - 15, 50, { align: 'right' });

            const price = parseFloat(product.price) || 0;
            const vat = price * 0.15;
            const total = price + vat;
            const tableColumn = ["Description", "Listing #", "Unit Price", "VAT (15%)", "Total"];
            const tableRows = [[product.name, product.listing_number, `R ${price.toFixed(2)}`, `R ${vat.toFixed(2)}`, `R ${total.toFixed(2)}`]];
            
            doc.autoTable({
                head: [tableColumn],
                body: tableRows,
                startY: 60,
                headStyles: { fillColor: [22, 160, 133] },
                styles: { textColor: [33, 33, 33] },
            });
            const finalY = doc.autoTable.previous.finalY;

            // Add product image if available
            if (productImage) {
                const imgSize = 50;
                const imgX = 15;
                const requiredHeight = imgSize + 20; // Image + caption + padding
                
                let imgY = finalY + 10;
                
                // Add new page if not enough space for image
                if (imgY + requiredHeight > 280) {
                    doc.addPage();
                    imgY = 20;
                }
                
                doc.addImage(productImage, 'JPEG', imgX, imgY, imgSize, imgSize);
                
                // Add image caption
                doc.setFontSize(8);
                doc.setFont('helvetica', 'italic');
                doc.setTextColor(100);
                doc.text('Product Image', imgX, imgY + imgSize + 5);
            }

            // --- LAST PAGE: Banking Details ---
            doc.addPage();
            let lastPageY = 20;
            console.log('[PDF Generation FullScreen] Adding banking details as last page');

            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text('Banking Details:', 15, lastPageY);
            console.log('[PDF Generation FullScreen] Adding banking details');
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            
            // Check if this is underground equipment category (standard or rebuild)
            const undergroundCategoryIds = [2, 33, 34, 35, 36, 37, 38, 39, 40];
            const isUndergroundEquipment = undergroundCategoryIds.includes(product.category_id);
            
            if (isUndergroundEquipment) {
                // Underground equipment specific banking details
                doc.text('Bank Name: FIRST NATIONAL BANK', 15, lastPageY + 7);
                doc.text('Account Type: GOLD BUSINESS ACCOUNT', 15, lastPageY + 14);
                doc.text('Account Number: 63204739213', 15, lastPageY + 21);
                doc.text('Branch Code: 258155', 15, lastPageY + 28);
                doc.text('Branch Name: FNB POP BRANCH OLYMPUS', 15, lastPageY + 35);
                doc.text('SWIFT Code: FIRNZAJJ', 15, lastPageY + 42);
            } else {
                // Default banking details
                doc.text('Bank Name: FIRST NATIONAL BANK', 15, lastPageY + 7);
                doc.text('Account Type: GOLD BUSINESS ACCOUNT', 15, lastPageY + 14);
                doc.text('Account Number: 63204739205', 15, lastPageY + 21);
                doc.text('Branch Code: 258155', 15, lastPageY + 28);
                doc.text('Branch Name: FNB POP BRANCH DOLYMPUS', 15, lastPageY + 35);
                doc.text('SWIFT Code: FIRNZAJJ', 15, lastPageY + 42);
            }
            doc.setFont('helvetica', 'bold');
            doc.text('Payment Reference: Please use listing number ' + product.listing_number, 15, lastPageY + 49);

            // --- Add Terms and Conditions ---
            lastPageY += 65;
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(33, 33, 33);
            doc.text('Terms and Conditions:', 15, lastPageY);
            console.log('[PDF Generation FullScreen] Adding terms and conditions');

            // Add T&C link note
            doc.setFont('helvetica', 'italic');
            doc.setFontSize(7);
            doc.setTextColor(100, 100, 100);
            doc.text("PLEASE USE LINK TO VIEW", 195, lastPageY, { align: 'right' });

            doc.setLineWidth(0.5);
            doc.setDrawColor(200, 200, 200);
            doc.line(15, lastPageY + 2, 195, lastPageY + 2);
            lastPageY += 10;

            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(60, 60, 60);

            const addTermSection = (title, content) => {
                doc.setFont('helvetica', 'bold');
                doc.text(title, 15, lastPageY);
                lastPageY += 5;
                doc.setFont('helvetica', 'normal');
                const lines = doc.splitTextToSize(content, 180);
                doc.text(lines, 15, lastPageY);
                lastPageY += (lines.length * 4) + 4;
            };

            addTermSection('Quotation Currency', 'THIS QUOTATION IS CALCULATED IN SOUTHERN AFRICAN CURRENCY (ZAR)');
            addTermSection('Pricing', '• Prices quoted include VAT.\n• Any optional accessories, add-ons, or customisations requested by the customer will be quoted separately and may incur additional charges.');
            addTermSection('Validity', '• This quotation is valid for five (5) days from the date of issue.\n• Prices may be subject to change after the validity period expires.');
            addTermSection('Payment Terms', '• Accepted payment methods:\n   o Bank Transfer (EFT)\n   o Credit Card\n   o Cheque\n   (Trade Safe Payment Options Available via Miners Market)\n• Full payment is required before the machine/equipment will be released.');
            addTermSection('Delivery', '• Delivery arrangements, costs, and timelines will be discussed and confirmed with the Miners Market team upon POP confirmation. Inspection of equipment/spares can be arranged on request.');
            addTermSection('General', '• This quotation is subject to Miners Market\'s standard terms and conditions.\n• Availability of equipment is subject to prior sale.');

            lastPageY += 5;
            doc.setFont('helvetica', 'italic');
            const closingText = doc.splitTextToSize('We look forward to doing business with you and building a long-standing working relationship with you and your company.', 180);
            doc.text(closingText, 15, lastPageY);
            lastPageY += (closingText.length * 4) + 10;

            // --- Contact Section ---
            if (lastPageY > 240) { doc.addPage(); lastPageY = 20; }
            console.log('[PDF Generation FullScreen] Adding contact section');
            doc.setFillColor(248, 249, 250);
            doc.setDrawColor(220, 220, 220);
            doc.rect(15, lastPageY, 180, 45, 'FD');

            const footerLogoSize = 25;
            const footerLogoX = 20;
            const footerLogoY = lastPageY + 10;
            doc.addImage(circularLogoData, 'PNG', footerLogoX, footerLogoY, footerLogoSize, footerLogoSize);
            doc.setDrawColor(200, 200, 200);
            doc.setLineWidth(0.5);
            doc.circle(footerLogoX + footerLogoSize / 2, footerLogoY + footerLogoSize / 2, footerLogoSize / 2, 'S');

            let contactY = lastPageY + 8;
            const col1 = 50; const col2 = 115;
            doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(33, 33, 33);
            doc.text('The Miners Market Team', col1, contactY);
            contactY += 8; doc.setFontSize(8); doc.setFont('helvetica', 'normal');
            doc.text('Phone: +27 82 455 1658', col1, contactY); contactY += 5;
            doc.text('Email: info@minersmarket.co.za (General Enquiries)', col1, contactY); contactY += 5;
            doc.text('Email: finance@minersmarket.co.za (Finance)', col1, contactY); contactY += 5;
            let socialX = col1; doc.text('Social: ', socialX, contactY); socialX += doc.getTextWidth('Social: ');
            doc.setTextColor(0, 0, 255); doc.text('LinkedIn', socialX, contactY, { url: 'https://www.linkedin.com/company/miners-market/posts/?feedView=all' }); socialX += doc.getTextWidth('LinkedIn');
            doc.setTextColor(33, 33, 33); doc.text(' | ', socialX, contactY); socialX += doc.getTextWidth(' | ');
            doc.setTextColor(0, 0, 255); doc.text('Facebook', socialX, contactY, { url: 'https://www.facebook.com/people/Miners-Market/61581241269225/' }); socialX += doc.getTextWidth('Facebook');
            doc.setTextColor(33, 33, 33); doc.text(' | ', socialX, contactY); socialX += doc.getTextWidth(' | ');
            doc.setTextColor(0, 0, 255); doc.text('WhatsApp', socialX, contactY, { url: 'https://whatsapp.com/channel/0029VbAgvIQHAdNVnXCaN30z' });
            doc.setTextColor(33, 33, 33);
            contactY = lastPageY + 16;
            doc.text('Web: www.minersmarket.co.za', col2, contactY, { url: 'https://www.minersmarket.co.za' }); contactY += 5;
            doc.text('Email: quotes@minersmarket.co.za (Quotations)', col2, contactY); contactY += 5;
            doc.text('Email: support@minersmarket.co.za (Technical)', col2, contactY);

            // Convert PDF to base64
            const pdfData = doc.output('datauristring').split(',')[1];
            console.log('[PDF Generation FullScreen] PDF data generated, length:', pdfData.length, 'bytes');

            // Send email with PDF - using async endpoint for instant response
            const response = await fetch('/api/send_quote_email_async.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    recipient_email: recipientEmail,
                    recipient_name: user.name,
                    product_id: product.id,
                    listing_type: product.listingType,
                    product_name: product.name,
                    listing_number: product.listing_number,
                    pdf_data: pdfData
                })
            });
            
            const result = await response.json();
            if (!response.ok) throw new Error(result.error || 'Failed to send quote.');
            setActionSuccess(result.message);
            setShowQuoteConfirmModal(false);
        } catch (err) {
            setActionError(err.message);
        } finally {
            setIsProcessing(false);
        }
    };

    // --- NEW: Function to generate Rebuild PDF ---
    const generateRebuildPdf = async () => {
        try {
            const { jsPDF } = window.jspdf;
            const doc = new jsPDF();

            // Helper to load images
            const loadImage = (src) => new Promise((resolve) => {
                const img = new Image();
                img.src = src;
                img.onload = () => resolve(img);
                img.onerror = () => {
                    console.warn(`Failed to load image: ${src}`);
                    resolve(null);
                };
            });

            // --- NEW: Load User-Supplied Main Images ---
            const userImageUrls = [product.image_url_1, product.image_url_2, product.image_url_3, product.image_url_4].map(cleanPath).filter(Boolean);
            const mainImages = await Promise.all(userImageUrls.map(loadImage));

            // --- NEW: Load Matrix Component Images ---
            const matrixImageMap = {};
            const matrixPromises = [];
            Object.entries(sellerRebuildDetails).forEach(([comp, details]) => {
                const urls = (details.images || (details.image_url ? [details.image_url] : [])).map(cleanPath).filter(Boolean);
                if (urls.length > 0) {
                    matrixPromises.push(Promise.all(urls.map(loadImage)).then(imgs => {
                        matrixImageMap[comp] = imgs.filter(Boolean);
                    }));
                }
            });
            await Promise.all(matrixPromises);

            // Load all assets (Logo + 5 parts)
            const [minersMarketLogo, part1, part2, part3, part4, part6] = await Promise.all([
                loadImage('images/logo/MM - Logo.jpeg'),
                loadImage('images/equipmentrebuildpdf/part1.png'),
                loadImage('images/equipmentrebuildpdf/part2.png'),
                loadImage('images/equipmentrebuildpdf/part3.png'),
                loadImage('images/equipmentrebuildpdf/part4.png'),
                loadImage('images/equipmentrebuildpdf/part6.png')
            ]);

            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const addFullPage = (img) => {
                if (img) doc.addImage(img, 'PNG', 0, 0, pageWidth, pageHeight);
            };

            // --- Part 1 ---
            addFullPage(part1);

            // --- Part 2 ---
            doc.addPage();
            addFullPage(part2);

            // --- Part 3 ---
            doc.addPage();
            addFullPage(part3);

            // --- Part 4 ---
            doc.addPage();
            addFullPage(part4);

            // --- Part 5: Dynamic Content Start ---
            doc.addPage();
            
            // Add Logo
            if (minersMarketLogo) {
                try {
                    doc.addImage(minersMarketLogo, 'JPEG', 15, 15, 30, 30);
                } catch (e) {
                    console.warn("Logo add failed", e);
                }
            }

            doc.setFontSize(20);
            doc.setFont('helvetica', 'bold');
            doc.text('Rebuild Status Inspection', 105, 25, { align: 'center' });
            
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text(`Listing #: ${product.listing_number}`, 105, 32, { align: 'center' });
            doc.text(`Date: ${new Date().toLocaleDateString()}`, 195, 25, { align: 'right' });

            let yPos = 55;

            // Product Info
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text('Equipment Details', 15, yPos);
            yPos += 5;
            
            const infoData = [
                ['Item Name', product.name],
                ['Manufacturer', product.manufacturer],
                ['Model', product.model_number],
                ['Serial Number', product.serial_number],
                ['Description', product.description],
                ['Year', product.year?.toString() || 'N/A'],
                ['Hours', `${product.hours?.toString() || 'N/A'} (N/A, LIFE of EQUIPMENT)`],
                ['Location', product.location]
            ];

            doc.autoTable({
                startY: yPos,
                head: [['Field', 'Value']],
                body: infoData,
                theme: 'striped',
                headStyles: { fillColor: [41, 128, 185] },
                styles: { fontSize: 10 },
                columnStyles: { 0: { fontStyle: 'bold', cellWidth: 60 } }
            });

            yPos = doc.lastAutoTable.finalY + 15;

            // --- NEW: Insert Main Product Images ---
            const validMainImages = mainImages.filter(Boolean);
            if (validMainImages.length > 0) {
                // Check for space
                if (yPos + 50 > pageHeight - 20) { doc.addPage(); yPos = 20; }

                doc.setFontSize(12);
                doc.setFont('helvetica', 'bold');
                doc.text('Equipment Photos', 15, yPos);
                yPos += 7;

                let imgX = 15;
                validMainImages.forEach((img) => {
                    if (imgX + 40 > pageWidth - 15) return; // Prevent overflow
                    try {
                        // Let jsPDF auto-detect the image type (PNG, JPEG, or WEBP)
                        doc.addImage(img, imgX, yPos, 40, 40);
                        imgX += 45;
                    } catch (e) { console.warn("Image add error", e); }
                });
                yPos += 45; // Advance Y position past images
            }

            // Rebuild Matrix
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text('Component Status Inspection', 15, yPos);
            yPos += 5;

            // Helper to get column config per category
            const getCategoryConfig = (category) => {
                const v = (val) => (val === undefined || val === null || val === '') ? '-' : val;
               switch (category) {
                    case "Powertrain & Drivetrain":
                        return {
                            headers: ['Component', 'Condition', 'Runner', 'Hours', 'Description / Notes', 'Images'],
                            map: (d) => [v(d.condition), v(d.runner), d.hours_na ? 'N/A' : v(d.hours), v(d.comment)]
                        };
                    case "Hydraulic, Fuel & Fluid Systems":
                        return {
                            headers: ['Component', 'Condition', 'System Status', 'Description / Notes', 'Images'],
                            map: (d) => [v(d.condition), v(d.system_status), v(d.comment)]
                        };
                    case "Electrical & Electronic Systems":
                        return {
                            headers: ['Component', 'Installed', 'Operational Status', 'Description / Notes', 'Images'],
                            map: (d) => [v(d.installed), v(d.operational_status), v(d.comment)]
                        };
                    case "Structure, Chassis & Frame":
                        return {
                            headers: ['Component', 'Condition', 'Damage Type', 'Description / Notes', 'Images'],
                            map: (d) => {
                                const damageTypes = d.damage_types ? Object.keys(d.damage_types).filter(k => d.damage_types[k]).join(',\n') : (d.damage_notes || '-');
                                return [v(d.condition), damageTypes, v(d.comment)];
                            }
                        };
                    case "Safety Systems":
                        return {
                            headers: ['Component', 'Installed', 'Operational', 'Compliance', 'Description / Notes', 'Images'],
                            map: (d) => [v(d.installed), v(d.operational), v(d.compliance_status), v(d.comment)]
                        };
                    case "Wear Components & Running Gear":
                        return {
                            headers: ['Component', 'Condition', 'Wear Level', 'Description / Notes', 'Images'],
                            map: (d) => [v(d.condition), v(d.wear_level), v(d.comment)]
                        };
                    case "Bodywork, Cab & Visibility":
                        return {
                            headers: ['Component', 'Condition', 'Corrosion', 'Description / Notes', 'Images'],
                            map: (d, item) => {
                                if (item === 'Windows' && d.cab_type === 'open') {
                                    return ['Open Cab', '-', '-', ''];
                                }
                                return [v(d.condition), v(d.corrosion_status), v(d.comment)];
                            }
                        };
                    case "Compliance, Legal & Identification":
                        return {
                            headers: ['Component', 'Present', 'Legible', 'Compliance', 'Description / Notes', 'Images'],
                            map: (d) => [v(d.present), v(d.legible), v(d.compliance_status), v(d.comment)]
                        };
                    default:
                        return {
                            headers: ['Component', 'Condition', 'Description / Notes', 'Images'],
                            map: (d) => [v(d.condition), v(d.comment)]
                        };
                }
            };

            let hasAnyData = false;

            for (const [category, items] of Object.entries(REBUILD_MATRIX_DATA)) {
                const activeItems = items.filter(item => sellerRebuildDetails[item]);


                if (activeItems.length === 0) continue;
                hasAnyData = true;

                const config = getCategoryConfig(category);

                // Prepare rows
                const rows = activeItems.map(item => {
                    const details = sellerRebuildDetails[item];
                    const itemName = (item.startsWith('Other ') && details.other_name) ? details.other_name : item.replace(/ \(.*/, '');

                    if (details.not_applicable) {
                        const emptyValues = new Array(config.headers.length - 2).fill('-');
                        emptyValues[0] = 'Not Applicable';
                        return [itemName, ...emptyValues, ''];
                    }

                    let comment = details.comment || '-';
                    if (details.additional_info) comment += `\nNote: ${details.additional_info}`;
                    
                    const dProxy = { ...details, comment };
                    const values = config.map(dProxy, item);
                    
                    return [itemName, ...values, '']; // Append empty for images column
                });

                // Check for page break
                if (yPos + 40 > pageHeight) {
                    doc.addPage();
                    yPos = 20;
                }

                // Draw Section Header with Background Color
                doc.setFillColor(230, 230, 230); // Light gray background
                doc.rect(15, yPos, 180, 8, 'F');
                doc.setFontSize(10);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(0);
                doc.text(category.toUpperCase(), 18, yPos + 5.5);
                yPos += 8;

                doc.autoTable({
                    startY: yPos,
                    head: [config.headers],
                    body: rows,
                    theme: 'grid',
                    headStyles: { fillColor: [44, 62, 80], fontSize: 8 },
                    styles: { fontSize: 8, cellPadding: 2, minCellHeight: 25, valign: 'middle' },
                    columnStyles: {
                        0: { cellWidth: 40, fontStyle: 'bold' },
                        [config.headers.length - 1]: { cellWidth: 45 } // Image column is always last
                    },
                    didDrawCell: (data) => {
                        // Render images in the last column
                        if (data.section === 'body' && data.column.index === config.headers.length - 1) {
                            const compName = activeItems[data.row.index]; // Map row index back to item key
                            const imgs = matrixImageMap[compName];
                            if (imgs && imgs.length > 0) {
                                let x = data.cell.x + 2;
                                let y = data.cell.y + 2;
                                const size = 20; // Reasonable thumbnail size
                                imgs.forEach(img => {
                                    if (x + size < data.cell.x + data.cell.width) {
                                        try { 
                                            // Let jsPDF auto-detect the image type (PNG, JPEG, or WEBP)
                                            doc.addImage(img, x, y, size, size);
                                            x += size + 2;
                                        } catch (e) {
                                            console.warn("Failed to add matrix image to PDF", e);
                                        }
                                    }
                                });
                            }
                        }
                    }
                });
                yPos = doc.lastAutoTable.finalY + 10;
            }

            if (!hasAnyData) {
                doc.setFont('helvetica', 'italic');
                doc.setFontSize(10);
                doc.text('No specific rebuild details declared (Sold AS-IS).', 15, yPos + 10);
                yPos += 15;
            }

            // --- NEW: Add Inspection & Service Section ---
            const inspectionServiceItems = [
                "Full Service", "Operational Testing", "Safety Audit", "Final Quality Sign-Off",
                "Good clean runner", "Rebuild to zero base", "Spares only/Service exchange", "As-is", "Custom requirement"
            ];
            const inspectionRows = [];
            inspectionServiceItems.forEach(item => {
                const details = sellerRebuildDetails[item];
                if (details) {
                    let value = details.selected === 'yes' ? 'Yes' : (details.selected === 'no' ? 'No' : '-');
                    if (item === "Operational Testing" && details.comment) {
                        value += `\n(${details.comment})`;
                    }
                    inspectionRows.push([item, value]);
                }
            });

            if (inspectionRows.length > 0) {
                if (yPos + 20 > pageHeight) { doc.addPage(); yPos = 20; }
                doc.autoTable({
                    startY: yPos + 10,
                    head: [['Final Inspection & Condition', 'Status']],
                    body: inspectionRows,
                    theme: 'striped',
                    headStyles: { fillColor: [41, 128, 185] },
                });
            }

            // --- Part 6: Final Page ---
            doc.addPage();
            addFullPage(part6);

            // Footer (Only on dynamic content pages: 5 to N-1)
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 5; i < pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(150);
                doc.text(`Generated by Miners Market - Page ${i - 4} of ${pageCount - 5}`, 105, 287, { align: 'center' });
            }

            doc.save(`Rebuild_Status_${product.listing_number}.pdf`);

        } catch (err) {
            console.error("PDF Generation Error:", err);
          alert("Failed to generate PDF summary.");
        }
    };

    const handleTradeSafePurchase = async () => {
        setIsProcessing(true);
        setActionError(null);
        if (gateAction()) { setIsProcessing(false); return; }
        if (!user.name || !user.email || !user.phone_number) {
            setActionError("Please complete your name, email, and phone number in your profile to proceed.");
            setIsProcessing(false);
            return;
        }
        if (!product.sales_contact_name || !product.sales_contact_email || !product.sales_contact_phone) {
            setActionError("Seller contact information is missing. Cannot proceed.");
            setIsProcessing(false);
            return;
        }
        try {
            const body = {
                product_title: product.name,
                product_desc: product.description || 'No description provided.',
                amount: totalAmount,
                product_id: product.id,
                buyer_id: user.id,
                seller_id: product.seller_id,
                listingType: product.listingType,
                quantity: 1, // For single item purchases
                buyer_name: user.name,
                buyer_email: user.email,
                buyer_phone: user.phone_number,
                seller_name: product.sales_contact_name,
                seller_email: product.sales_contact_email,
                seller_phone: product.sales_contact_phone
            };
            const res = await fetch('/api/initiate_payment.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const result = await res.json();
            if (!res.ok) throw new Error(result.error || 'Failed to initiate payment.');
            if (!result.checkoutUrl) throw new Error('Checkout URL not received from server.');
            window.location.href = result.checkoutUrl;
        } catch (err) {
            setActionError(err.message);
            setIsProcessing(false);
        }
    };

    // --- REPLICATED FROM ProductCard.js ---
    const renderPriceSection = () => {
        if (canRequestPrice) {
            return (
                <div className="w-full flex flex-col items-center gap-2">
                    <button 
                        onClick={handleRequestPrice} 
                        disabled={isProcessing || priceRequested}
                        className="w-full primary-gradient text-white font-bold py-3 px-4 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                    >
                        {isProcessing ? <><i className="fas fa-spinner fa-spin mr-2"></i>Requesting...</> : (priceRequested ? 'Price Requested' : 'Request Price')}
                    </button>
                    {priceRequested ? (
                        <p className="text-xs text-gray-500 text-center">As soon as price has been accepted please check your Buyer Dashboard</p>
                    ) : (
                        <p className="text-xs text-gray-500 text-center">Click to reveal price.</p>
                    )}
                </div>
            );
        }
        if (product.is_price_on_request && (product.price === null || product.listingType === 'service')) {
            return (
                <div><p className="text-3xl font-bold text-blue-600">POR</p></div>
            );
        }
        if (product.listingType === 'consumable' && availableSizes) {
            const prices = Object.keys(availableSizes).map(getSizeUnitPrice);
            const minPrice = prices.length ? Math.min(...prices) : Number(product.price) || 0;
            const maxPrice = prices.length ? Math.max(...prices) : minPrice;
            const hasPriceRange = minPrice !== maxPrice;

            return (
                <div>
                    <p className="text-3xl font-bold text-blue-600">
                        {hasPriceRange
                            ? `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`
                            : formatPrice(minPrice)}
                    </p>
                    <p className="text-xs text-gray-500">
                        {hasPriceRange ? 'Unit Price Range (incl. fee)' : 'Unit Price (incl. fee)'}
                    </p>
                    {selectedQuantity > 0 ? (
                        <div className="mt-3">
                            <p className="text-sm text-gray-600">Selected quantity: {selectedQuantity}</p>
                            <p className="text-2xl font-bold text-blue-600">
                                Total: {formatPrice(selectedTotal)}
                            </p>
                        </div>
                    ) : (
                        <p className="mt-2 text-sm text-gray-500">Please select sizes and quantities.</p>
                    )}
                </div>
            );
        }
        return (
            <div>
                <p className="text-3xl font-bold text-blue-600">{formatPrice(product.price)}</p>
                <p className="text-xs text-gray-500">{product.listingType === 'rebuild' ? 'Base Price (Before Work Done)' : 'Total Price (incl. fee)'}</p>
            </div>
        );
    };

    // --- Price Breakdown Logic ---
    const platformFee = totalAmount * (window.APP_CONFIG?.platformFeePercentage || 0.06);
    const itemPrice = totalAmount - platformFee;


    const badgeSrc = {
        'wots': 'images/badges/MM Badge.png',
        'standard': 'images/badges/MM Badge.png'
    };

    const kpLink = '/kp-insurance';
    const isEquipment = ['standard', 'rebuild', 'rental'].includes(product.listingType) && !product.bearing_type && product.badgeType !== 'wots' && product.listing_market !== 'miscellaneous';
    // --- END REPLICATION ---

    // --- ACTION BUTTON VISIBILITY LOGIC ---
    const isPurchasable = ['standard', 'wots', 'mine', 'ore', 'conveyor', 'filter', 'grease', 'kg_pps'].includes(product.listingType);
    const isInquiryBased = ['rental', 'service'].includes(product.listingType);
    const isRebuild = product.listingType === 'rebuild';
    const isDiesel = product.listingType === 'diesel' || product.name?.toLowerCase().includes('diesel');
    const canRequestPrice = ['standard', 'wots', 'conveyor', 'filter', 'grease', 'kg_pps', 'service'].includes(product.listingType) && (product.is_price_on_request || !product.price || parseFloat(product.price) <= 0);

    // --- DOCUMENTS LOGIC ---
    const documents = [
        { name: 'User Manual', url: product.doc_manual_url },
        { name: 'Data Pack', url: product.doc_datasheet_url },
        { name: 'Certificate of Conformity', url: product.doc_conformity_url },
        { name: 'Calibration Certificate', url: product.doc_calibration_url },
        { name: 'Warranty Card', url: product.doc_warranty_url },
        { name: 'Service History', url: product.doc_service_history_url },
        { name: 'Factory Test Report', url: product.doc_factory_test_url },
        { name: 'Oil Analysis Report', url: product.doc_oil_report_url },
    ];

    // --- NEW: Merge documents from 'media' array ---
    if (product.media && Array.isArray(product.media)) {
        product.media.filter(m => m.media_type === 'document').forEach(doc => {
            documents.push({ name: doc.display_name || doc.category, url: doc.file_path });
        });
    }

    const validDocuments = documents.filter(doc => doc.url);

    // Helper to render a detail item only if it has a value
    const DetailItem = ({ label, value, formatAsDescription = false }) => {
        if (!value) return null;
        return (
            <div className="bg-gray-50 p-3 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-500 mb-1">{label}</h4>
                {formatAsDescription ? (
                    <div className="text-gray-800 break-words">{formatDescription(value)}</div>
                ) : (
                    <p className="text-gray-800 break-words">{value}</p>
                )}
            </div>
        );
    };

    // StarRating helper component to display star ratings
    const StarRating = ({ rating = 0, count = 0 }) => {
        const fullStars = Math.floor(rating);
        const halfStar = rating % 1 >= 0.5;
        const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);

        if (count === 0) {
            return <span className="text-sm text-gray-500">No ratings yet.</span>;
        }

        return (
            <div className="flex items-center space-x-2">
                <div className="flex text-amber-400">
                    {[...Array(fullStars)].map((_, i) => <i key={`full-${i}`} className="fas fa-star"></i>)}
                    {halfStar && <i className="fas fa-star-half-alt"></i>}
                    {[...Array(emptyStars)].map((_, i) => <i key={`empty-${i}`} className="far fa-star"></i>)}
                </div>
                <span className="text-sm text-gray-600">
                    {parseFloat(rating).toFixed(1)} ({count} {count === 1 ? 'rating' : 'ratings'})
                </span>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 md:p-4" onClick={onClose}>
            {/* Main Modal Container */}
            <div className="bg-white w-full h-full md:max-w-4xl md:h-auto md:max-h-[90vh] md:rounded-2xl flex flex-col overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
                
                {/* Header (Mobile and Desktop) */}
                <div className="flex-shrink-0 flex items-center justify-between p-4 border-b">
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-2xl">
                        <i className="fas fa-arrow-left"></i>
                        <span className="sr-only">Back</span>
                    </button>
                    <h2 className="text-lg font-semibold text-gray-800 truncate md:hidden">{product.name}</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-2xl md:hidden">
                        <i className="fas fa-times"></i>
                        <span className="sr-only">Close</span>
                    </button>
                    {/* --- NEW: Save Button --- */}
                    <button 
                        onClick={() => onToggleSave(product)}
                        className={`hidden md:block text-2xl transition-colors ${isSaved ? 'text-red-500' : 'text-gray-400 hover:text-red-400'}`}
                        title={isSaved ? "Unsave Item" : "Save for Later"}
                    >
                        <i className={`${isSaved ? 'fas' : 'far'} fa-heart`}></i>
                    </button>
                </div>

                {/* Scrollable Content Area */}
                <div className="flex-grow overflow-y-auto">
                    <div className="md:grid md:grid-cols-2 md:gap-8 p-4 md:p-8">
                        {/* Image Section - Replicated from ProductCard */}
                        <div>
                            <div 
                                className="relative"
                                onTouchStart={handleTouchStart}
                                onTouchEnd={handleTouchEnd}
                            >
                                <img 
                                    className="w-full aspect-square object-contain bg-gray-50 rounded-lg shadow-md" 
                                    src={optimizeImageSrc(imageError ? 'images/logo/MM - Logo.jpeg' : (mainImage || 'images/logo/MM - Logo.jpeg'), 1200)}
                                    alt={product.name} 
                                    onError={(e) => {
                                        if (imageError) return;
                                        console.warn(`Failed to load main modal image for product ${product.id}. URL: ${e.target.src}`);
                                        setImageError(true);
                                    }}
                                />
                                {productImages.length > 1 && (
                                    <>
                                        <button onClick={(e) => handleImageNav(e, -1)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-black/50 transition-colors z-10">
                                            <i className="fas fa-chevron-left text-lg"></i>
                                        </button>
                                        <button onClick={(e) => handleImageNav(e, 1)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-black/50 transition-colors z-10">
                                            <i className="fas fa-chevron-right text-lg"></i>
                                        </button>
                                    </>
                                )}
                                {productImages.length > 1 && (
                                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1.5 z-10">
                                        {productImages.slice(0, 20).map((_, index) => (
                                            <div key={index} className={`w-2 h-2 rounded-full transition-colors ${currentImageIndex === index ? 'bg-white' : 'bg-white/50'}`}></div>
                                        ))}
                                    </div>
                                )}
                                {product.bearing_type && product.brand && product.brand.toUpperCase().includes('KG') && (
                                     <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                        <img src={optimizeImageSrc('images/logo/kg-bearings-logo.webp', 120)} alt="KG Bearings" className="w-12 h-auto object-contain" />
                                     </div>
                                )}
                                {product.bearing_type && product.brand && product.brand.toUpperCase().includes('FAG') && (
                                     <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                        <img src={optimizeImageSrc('Miscellaneous Items/Logos-20260206T070056Z-3-001/Logos/FAG.png', 120)} alt="FAG Bearings" className="w-12 h-auto object-contain" />
                                     </div>
                                )}
                                {product.bearing_type && product.brand && product.brand.toUpperCase().includes('NTN') && (
                                     <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                        <img src={optimizeImageSrc('Miscellaneous Items/Logos-20260206T070056Z-3-001/Logos/NTN.png', 120)} alt="NTN Bearings" className="w-12 h-auto object-contain" />
                                     </div>
                                )}
                                {product.listingType === 'filter' && (
                                     <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                        <img src={optimizeImageSrc('Miscellaneous Items/Logos-20260206T070056Z-3-001/Logos/KG-Filter-high-res-logo blue.png', 200)} alt="KG Filters" className="w-24 h-auto object-contain" />
                                     </div>
                                )}
                                {product.listingType === 'conveyor' && (
                                     <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                        <img src={optimizeImageSrc('Miscellaneous Items/Logos-20260206T070056Z-3-001/Logos/arb-logo.png', 160)} alt="ARB" className="w-20 h-auto object-contain" />
                                     </div>
                                )}
                                {product.listingType === 'grease' && (
                                     <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                        <img src={optimizeImageSrc('images/logo/kg-bearings-logo.webp', 120)} alt="KG Ultra" className="w-12 h-auto object-contain" />
                                     </div>
                                )}
                                {product.listingType === 'kg_pps' && (
                                     <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                        <img src={optimizeImageSrc('images/logo/kg-bearings-logo.webp', 120)} alt="KG ULTRA" className="w-12 h-auto object-contain" />
                                     </div>
                                )}
                                {product.badgeType && badgeSrc[product.badgeType] && (
                                    <img 
                                        src={optimizeImageSrc(badgeSrc[product.badgeType], 100)} 
                                        alt={`${product.badgeType} listing badge`}
                                        className="absolute top-2 right-2 w-16 h-16 object-contain"
                                    />
                                )}
                                {/* --- NEW: Share Icon --- */}
                                <button
                                    onClick={handleShare}
                                    className="absolute bottom-4 left-4 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/30 backdrop-blur-md text-gray-900 border border-white/20 flex items-center justify-center transition-all hover:bg-white/50 z-20 shadow-lg"
                                    title="Share Listing"
                                >
                                    <i className="fas fa-share-alt text-lg"></i>
                                </button>
                            </div>
                            {productImages.length > 1 && (
                                <div className="mt-4 flex space-x-2 overflow-x-auto pb-2">
                                    {productImages.map((img, index) => (
                                        <div key={img + index} className="flex-shrink-0">
                                            <img
                                                src={optimizeImageSrc(img, 300)}
                                                alt={`Thumbnail ${index + 1}`}
                                                onClick={() => {
                                                    setMainImage(img);
                                                    setCurrentImageIndex(index);
                                                }}
                                                className={`w-20 h-20 object-cover rounded-md cursor-pointer border-2 ${currentImageIndex === index ? 'border-blue-500' : 'border-transparent'}`}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Details Section */}
                        <div className="mt-6 md:mt-0">
                            <div className="hidden md:block mb-4">
                                <h1 className="text-3xl font-black text-gray-900 tracking-tighter uppercase leading-none">{product.name}</h1>
                                <p className="text-xs font-bold text-blue-600 uppercase tracking-[0.2em] mt-2">{product.category_name}</p>
                                <p className="text-xs font-black text-blue-600 uppercase tracking-[0.2em] mt-2">{product.category_name}</p>
                            </div>
                            <p className="text-gray-500 mt-2 md:hidden">{product.category_name}</p>

                            {/* --- NEW: Rebuild Classification Display --- */}
                            {product.listingType === 'rebuild' && product.rebuild_classification && (() => {
                                const classification = REBUILD_CLASSIFICATIONS.find(c => c.id === product.rebuild_classification);
                                if (!classification) return null;
                                return (
                                    <div className={`p-4 mt-4 rounded-lg border ${classification.badgeClasses} flex items-start gap-4`}>
                                        <i className={`${classification.icon} text-2xl mt-1`}></i>
                                        <div>
                                            <h4 className={`font-bold text-lg ${classification.titleColor}`}>{classification.title}</h4>
                                            <p className={`text-sm ${classification.descColor}`}>{classification.desc}</p>
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* --- Brand Tags (Mirrored from ProductCard.js) --- */}
                            {product.bearing_type && (
                                <div className="mt-2 mb-2">
                                    <span className={`${product.brand && product.brand.includes('KG') ? 'bg-blue-900' : 'bg-gray-800'} text-white text-xs font-bold px-2 py-1 rounded uppercase tracking-wider`}>
                                        Brand: {product.brand || 'KG ULTRA'}
                                    </span>
                                </div>
                            )}
                            {product.listingType === 'filter' && (
                                <div className="mt-2 mb-2">
                                    <span className="bg-blue-900 text-white text-xs font-bold px-2 py-1 rounded uppercase tracking-wider">
                                        Brand: {product.brand || 'KG Filters'}
                                    </span>
                                </div>
                            )}
                            {product.listingType === 'conveyor' && (
                                <div className="mt-2 mb-2">
                                    <span className="bg-blue-900 text-white text-xs font-bold px-2 py-1 rounded uppercase tracking-wider">
                                        Brand: {product.brand || 'ARB PLUS'}
                                    </span>
                                </div>
                            )}
                            {product.listingType === 'grease' && (
                                <div className="mt-2 mb-2">
                                    <span className="bg-blue-900 text-white text-xs font-bold px-2 py-1 rounded uppercase tracking-wider">
                                        Brand: {product.brand || 'KG Ultra'}
                                    </span>
                                </div>
                            )}
                            {product.listingType === 'kg_pps' && (
                                <div className="mt-2 mb-2">
                                    <span className="bg-blue-900 text-white text-xs font-bold px-2 py-1 rounded uppercase tracking-wider">
                                        Brand: {product.brand || 'KG ULTRA'}
                                    </span>
                                </div>
                            )}
                            
                            <div className="mt-6">
                                {renderPriceSection()}
                            </div>

                            {/* --- NEW: Size Selector for Consumables --- */}
                            {availableSizes && (
                                <div className="mt-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
                                    <h4 className="text-sm font-bold text-gray-700 mb-3">Select Sizes & Quantity</h4>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                        {Object.entries(availableSizes).map(([size, stock]) => (
                                            <div key={size} className="flex flex-col bg-white p-3 rounded border shadow-sm">
                                                <div className="flex justify-between text-xs mb-2">
                                                    <span className="font-bold truncate mr-1" title={size}>{size}</span>
                                                    <span className="text-gray-500 truncate">{getSizeStock(size)} left</span>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <button
                                                        onClick={() => handleSizeQtyChange(size, -1)}
                                                        className="w-7 h-7 bg-gray-200 rounded hover:bg-gray-300 flex items-center justify-center text-gray-700"
                                                        disabled={!selectedSizes[size]}
                                                    >
                                                        -
                                                    </button>
                                                    <span className="font-semibold text-sm">{selectedSizes[size] || 0}</span>
                                                    <button
                                                        onClick={() => handleSizeQtyChange(size, 1)}
                                                        className="w-7 h-7 bg-blue-100 rounded hover:bg-blue-200 flex items-center justify-center text-blue-700"
                                                        disabled={(selectedSizes[size] || 0) >= getSizeStock(size)}
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="mt-6 space-y-4">
                                <h3 className="font-semibold text-gray-800 border-b pb-2">Details</h3>
                                
                                {/* Use rich description for bearings, otherwise standard description */}
                                {product.bearing_type ? getBearingRichDescription(product.bearing_type) : (
                                    <DetailItem label="Description" value={product.description} formatAsDescription={true} />
                                )}

                                {/* --- NEW: Consumable Details --- */}
                                {product.listingType === 'consumable' && (
                                    <>
                                        <DetailItem label="Description" value={product.description} formatAsDescription={true} />
                                        <DetailItem label="Brand" value={product.brand} />
                                        <DetailItem label="Size / Quantity" value={availableSizes ? "Please select sizes above" : product.size} />
                                    </>
                                )}

                                <DetailItem label="Listing #" value={product.listing_number} />
                                <DetailItem label="Condition" value={product.item_condition} />
                                <DetailItem label="Manufacturer" value={product.manufacturer} />
                                <DetailItem label="Model" value={product.model_number} />
                                <DetailItem label="Part #" value={product.part_number} />
                                <DetailItem label="Serial #" value={product.serial_number} />
                                <DetailItem label="Application" value={product.application} />
                                <DetailItem label="Quantity Available" value={product.quantity} />
                                <DetailItem label="Location" value={product.location} />
                                <DetailItem label="Shipping Terms" value={product.shipping_terms} />
                                <DetailItem label="Packaging" value={product.packaging} />
                                <DetailItem label="Estimated Delivery" value={product.delivery_time} />
                                
                                {product.specifications && <div className="bg-gray-50 p-3 rounded-lg">
                                    <h4 className="text-sm font-semibold text-gray-500 mb-1">Specifications</h4>
                                    <p className="text-gray-800 whitespace-pre-wrap">{product.specifications}</p>
                                </div>}
                                
                                {/* --- NEW: Component Checklist Display --- */}
                                {checklistItems && (
                                    <div className="bg-gray-50 p-3 rounded-lg">
                                        <h4 className="text-sm font-semibold text-gray-500 mb-2">Included Components</h4>
                                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                                            {checklistItems.map(item => (
                                                <li key={item} className="flex items-center text-sm text-gray-800">
                                                    <i className="fas fa-check-circle text-green-500 mr-2"></i>
                                                    <span>{item}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Grease Datasheet Download */}
                                {product.listingType === 'grease' && product.datasheet_url && (
                                    <div className="mt-4">
                                        <a href={product.datasheet_url} download className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                                            <i className="fas fa-file-download mr-2"></i> Download Datasheet
                                        </a>
                                    </div>
                                )}
                                
                                {/* KG PPS Datasheet Download */}
                                {product.listingType === 'kg_pps' && product.datasheet_url && (
                                    <div className="mt-4">
                                        <a href={product.datasheet_url} download className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                                            <i className="fas fa-file-download mr-2"></i> Download Datasheet
                                        </a>
                                    </div>
                                )}

                            {/* --- NEW: Technical Specifications Section --- */}
                            {(product.bearing_type || product.specs || product.listingType === 'conveyor' || product.listingType === 'filter' || product.listingType === 'grease' || product.listingType === 'kg_pps') && (
                                <div className="mt-8 pt-6 border-t border-gray-200">
                                    <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                                        <i className="fas fa-microchip mr-2 text-blue-600"></i> Technical Specifications
                                    </h3>
                                    <div className="grid grid-cols-2 gap-3">
                                        {(() => {
                                            const data = { ...product, ...(product.specs || {}) };
                                            const internalKeys = ['id', 'product_id', 'seller_id', 'category_id', 'created_at', 'updated_at', 'status', 'listing_type', 'listing_market', 'image_url_1', 'image_url_2', 'image_url_3', 'image_url_4', 'image_url_5', 'image_url_6', 'image_url_7', 'image_url_8', 'image_url_9', 'image_url_10', 'image_url_11', 'image_url_12', 'image_url_13', 'image_url_14', 'image_url_15', 'image_url_16', 'image_url_17', 'image_url_18', 'image_url_19', 'image_url_20', 'image_url_21', 'image_url_22', 'image_url_23', 'image_url_24', 'image_url_25', 'slug', 'name', 'description', 'price', 'original_price', 'quantity', 'is_price_on_request', 'listing_number', 'sales_contact_name', 'sales_contact_phone', 'sales_contact_email', 'location', 'seller_name', 'avg_rating', 'rating_count', 'category_name', 'badgeType', 'specs', 'image_url', 'type_description', 'media', 'datasheet_url', 'full_description', 'short_description', 'applications', 'features', 'technical_specs', 'temperature_range', 'shelf_life', 'storage_conditions', 'grease_type', 'filter_type', 'brand', 'manufacturer', 'model_number', 'part_number', 'serial_number', 'year', 'hours', 'rebuild_classification', 'rebuild_details', 'willing_to_sell_spares', 'application', 'origin', 'packaging', 'rate_per_hour', 'min_hire_duration', 'size', 'equipment_type', 'service_name', 'service_description', 'ore_grade', 'quantity_available', 'specifications', 'compatibility', 'shipping_terms', 'delivery_time', 'company_name_address', 'support_contact_details'];
                                            
                                            return Object.entries(data)
                                                .filter(([key, value]) => !internalKeys.includes(key) && value && value !== 'NA' && value !== '0.00' && (typeof value === 'string' || typeof value === 'number'))
                                                .map(([key, value]) => (
                                                    <div key={key} className="bg-gray-50 p-2 rounded border border-gray-100">
                                                        <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider">{key.replace(/_/g, ' ')}</span>
                                                        <span className="block text-sm text-gray-800 font-medium break-words">{value}</span>
                                                    </div>
                                                ));
                                        })()}
                                    </div>
                                </div>
                            )}

                                {/* --- NEW: Rebuild Status Inspection Matrix --- */}
                                {isRebuild && Object.keys(sellerRebuildDetails).length > 0 && (
                                    <div className="mt-8 pt-6 border-t border-gray-200">
                                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b pb-2 mb-4 gap-4">
                                            <h3 className="font-semibold text-gray-800">Rebuild Status Inspection</h3>
                                            <button 
                                                onClick={generateRebuildPdf}
                                                className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center shadow-sm w-full sm:w-auto justify-center"
                                            >
                                                <i className="fas fa-file-pdf text-red-500 mr-2"></i> Download Full Report
                                            </button>
                                        </div>
                                        <div className="space-y-6">
                                            {Object.entries(REBUILD_MATRIX_DATA).map(([category, items]) => {
                                                const activeItems = items.filter(item => sellerRebuildDetails[item]);
                                                if (activeItems.length === 0) return null;

                                                return (
                                                    <div key={category} className="border rounded-lg overflow-hidden">
                                                        <div className="bg-gray-100 px-4 py-2 font-bold text-sm text-gray-800">{category}</div>
                                                        <div className="p-4 space-y-4">
                                                            {activeItems.map(item => {
                                                                const details = sellerRebuildDetails[item];
                                                                if (details.not_applicable) {
                                                                    return null;
                                                                }
                                                                return (
                                                                    <div key={item} className="border-b last:border-0 pb-4 last:pb-0">
                                                                        <div className="flex justify-between items-start">
                                                                            <div className="flex-grow pr-4">
                                                                                <p className="font-bold text-sm text-gray-900">{details.other_name || item.replace(/ \(.*/, '')}</p>
                                                                                <div className="text-xs text-gray-600 mt-2 space-y-1">
                                                                                    <DetailDisplay label="Condition" value={details.condition} />
                                                                                    <DetailDisplay label="Runner" value={details.runner} />
                                                                                    <DetailDisplay label="Hours" value={details.hours_na ? 'N/A' : details.hours} />
                                                                                    <DetailDisplay label="System Status" value={details.system_status} />
                                                                                    <DetailDisplay label="Installed" value={details.installed} />
                                                                                    <DetailDisplay label="Operational Status" value={details.operational_status} />
                                                                                    <DetailDisplay label="Damage Type" value={details.damage_types ? Object.keys(details.damage_types).filter(k => details.damage_types[k]).join(', ') : details.damage_notes} />
                                                                                    <DetailDisplay label="Operational" value={details.operational} />
                                                                                    <DetailDisplay label="Compliance" value={details.compliance_status} />
                                                                                    <DetailDisplay label="Wear Level" value={details.wear_level} />
                                                                                    <DetailDisplay label="Corrosion" value={details.corrosion_status} />
                                                                                    <DetailDisplay label="Present" value={details.present} />
                                                                                    <DetailDisplay label="Legible" value={details.legible} />
                                                                                    
                                                                                    {details.comment && details.comment !== 'N/A' && <p className="italic mt-1">"{details.comment}"</p>}
                                                                                    {details.additional_info && <p className="text-xs text-gray-500 mt-1">Note: {details.additional_info}</p>}
                                                                                </div>
                                                                            </div>
                                                                            <div className="flex flex-wrap gap-2">
                                                                                {(details.images || (details.image_url ? [details.image_url] : [])).map((url, idx) => (
                                                                                    <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                                                                                        <img src={url} alt={`${item} ${idx + 1}`} className="w-12 h-12 object-cover rounded border hover:opacity-75 transition-opacity" />
                                                                                    </a>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Documents Section */}
                            {validDocuments.length > 0 && (
                                <div className="mt-6 pt-6 border-t">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Attached Documents</h3>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        {validDocuments.map(doc => (
                                            <a key={doc.name} href={doc.url} target="_blank" rel="noopener noreferrer" className="bg-gray-100 p-3 rounded-lg flex items-center space-x-3 hover:bg-gray-200 transition-colors">
                                                <i className="fas fa-file-alt text-gray-500 text-xl"></i>
                                                <span className="text-sm font-medium text-gray-800">{doc.name}</span>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="mt-6 border-t pt-4 space-y-4">
                                <h3 className="font-semibold text-gray-800">Seller Rating</h3>
                                <StarRating rating={product.avg_rating || 0} count={product.rating_count || 0} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer (Mobile and Desktop) */}
                <div className="flex-shrink-0 p-4 border-t bg-gray-50">
                    {actionError && <div className="bg-red-100 text-red-700 p-3 rounded-lg text-sm mb-4">{actionError}</div>}
                    {actionSuccess && <div className="bg-green-100 text-green-700 p-3 rounded-lg text-sm mb-4">{actionSuccess}</div>}

                    {/* Basket Product Actions */}
                    {isBasketProduct && (!user || user?.activeRole === 'buyer') && (
                        <div className="flex items-center justify-between gap-4">
                        {quantityInBasket === 0 ? (
                            <button 
                                onClick={handleAddToCart}
                                className="flex-grow primary-gradient text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:opacity-90 transition-opacity flex items-center justify-center"
                            >
                                <i className="fas fa-shopping-basket mr-2"></i> Add to Basket
                            </button>
                        ) : (
                            <div className="flex items-center justify-between w-full bg-white border rounded-lg p-1">
                                <button onClick={handleDecreaseQuantity} className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700 font-bold text-lg">-</button>
                                <span className="font-bold text-xl text-gray-900">{quantityInBasket}</span>
                                <button onClick={handleIncreaseQuantity} className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700 font-bold text-lg">+</button>
                            </div>
                        )}
                        </div>
                    )}

                    {/* Standard/Rebuild/Etc Product Actions */}
                    {(!isBasketProduct && (!user || user?.activeRole === 'buyer')) && (
                        showPriceBreakdown ? (
                            <div className="animate-fade-in">
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 space-y-3 text-sm">
                                    <div className="flex justify-between"><span>Item Price:</span><span className="font-medium">{formatPrice(itemPrice)}</span></div>
                                    <div className="flex justify-between"><span>Platform Fee:</span><span className="font-medium">{formatPrice(platformFee)}</span></div>
                                    <div className="flex justify-between font-bold text-base border-t pt-2 mt-2"><span>Total Amount: [VAT EXCL]</span><span>{formatPrice(totalAmount)}</span></div>
                                </div>
                                <div className="flex flex-wrap justify-end gap-4">
                                    <button onClick={() => setShowPriceBreakdown(false)} className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition-colors">Back</button>
                                    <button 
                                        onClick={handleTradeSafePurchase}
                                        disabled={isProcessing}
                                        className="flex-1 primary-gradient text-white py-3 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                                    >
                                        {isProcessing ? 'Connecting...' : 'Confirm & Pay'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col sm:flex-row gap-2">
                                <button onClick={handleContactAdmin} className="flex-1 bg-gray-200 text-gray-800 font-bold py-2 px-3 rounded-lg hover:bg-gray-300 transition-colors text-sm">
                                    <i className="fas fa-envelope mr-1"></i>Contact Admin
                                </button>
                                {isDiesel ? (
                                    <button onClick={handleDieselQuote} className="flex-1 primary-gradient text-white font-bold py-2 px-3 rounded-lg hover:opacity-90 transition-opacity text-sm">
                                        <i className="fas fa-gas-pump mr-1"></i>Request Diesel Quote
                                    </button>
                                ) : !canRequestPrice && (
                                    <button onClick={handleRequestQuote} className="flex-1 bg-blue-600 text-white font-bold py-2 px-3 rounded-lg hover:bg-blue-700 transition-opacity text-sm">
                                        <i className="fas fa-file-invoice-dollar mr-1"></i>Request Quote
                                    </button>
                                )}
                            </div>
                        )
                    )}

                    {/* Fallback for other cases */}
                    {!isBasketProduct && user && user.activeRole !== 'buyer' && (
                        <p className="text-sm text-gray-500 w-full text-center">Actions are available in Buyer mode.</p>
                    )}
                    {!isBasketProduct && !isPurchasable && !isInquiryBased && !isRebuild && (
                        <p className="text-sm text-gray-500 w-full text-center">Further actions available on the product page.</p>
                    )}

                    <button onClick={onClose} className="md:hidden w-full mt-4 text-center text-gray-600 font-medium py-2">
                        Back to Marketplace
                    </button>
                    {/* --- NEW: Save Button for Mobile --- */}
                    <button 
                        onClick={() => onToggleSave(product)}
                        className={`md:hidden w-full mt-2 text-center font-medium py-2 flex items-center justify-center gap-2 transition-colors ${isSaved ? 'text-red-500' : 'text-gray-600'}`}
                    >
                        <i className={`${isSaved ? 'fas' : 'far'} fa-heart`}></i>
                        <span>{isSaved ? 'Saved' : 'Save for Later'}</span>
                    </button>
                </div>
            </div>

            {/* Contact Admin Modal */}
            {showContactModal && <ContactAdminModal
                isOpen={showContactModal}
                onClose={() => setShowContactModal(false)}
                product={product}
                user={user}
            />}
            {/* Diesel Quote Modal */}
            {showDieselQuoteModal && <DieselQuoteModal
                isOpen={showDieselQuoteModal}
                onClose={() => setShowDieselQuoteModal(false)}
                product={product}
                onSendQuote={sendDieselQuote}
            />}

            {/* --- NEW: Quote Confirmation Modal --- */}
            {showQuoteConfirmModal && <QuoteConfirmationModal
                isOpen={showQuoteConfirmModal}
                onClose={() => setShowQuoteConfirmModal(false)}
                userEmail={user.email}
                onConfirm={confirmAndRequestQuote}
            />}
        </div>
    );
}
